/**
 * AI-Powered Service Description Generator
 * Generates unique, personalized descriptions for service providers
 */

// Variety of writing styles and tones
const WRITING_STYLES = {
  professional: {
    intro: [
      "We are specialized in providing",
      "Our company offers premium",
      "We deliver exceptional",
      "Our team provides professional",
      "We specialize in offering high-quality"
    ],
    experience: [
      "With {exp} years of industry experience, we have mastered the art of",
      "Our {exp}+ years in the business have taught us how to deliver",
      "Having served clients for {exp} years, we understand exactly what makes",
      "Through {exp} years of dedicated service, we've perfected our approach to"
    ],
    quality: [
      "We pride ourselves on attention to detail and commitment to excellence.",
      "Quality and customer satisfaction are at the heart of everything we do.",
      "Our work reflects our dedication to perfection and client happiness.",
      "We maintain the highest standards in every project we undertake."
    ]
  },
  friendly: {
    intro: [
      "We love creating amazing",
      "Our passion is making your event special with",
      "We're here to bring you the best",
      "Let us help you with incredible",
      "We enjoy providing beautiful"
    ],
    experience: [
      "After {exp} wonderful years in this business, we know how to make",
      "We've been making people smile for {exp} years with our",
      "{exp} years of happy customers have taught us what really matters in",
      "Over {exp} years, we've learned all the secrets to perfect"
    ],
    quality: [
      "Your happiness is our biggest reward!",
      "We treat every event like it's our own celebration.",
      "Making your day special is what we do best.",
      "We put our heart into everything we create for you."
    ]
  },
  traditional: {
    intro: [
      "Our family has been serving with",
      "We carry forward the tradition of providing",
      "With traditional values and modern approach, we offer",
      "Rooted in culture, we provide authentic",
      "Our heritage in event services includes"
    ],
    experience: [
      "For {exp} generations, our family has been perfecting",
      "Our {exp} years of legacy brings you time-tested",
      "Through {exp} years of traditional service, we've preserved the art of",
      "With {exp} years of cultural expertise, we deliver authentic"
    ],
    quality: [
      "We honor traditional craftsmanship in every detail.",
      "Our services blend cultural authenticity with modern quality.",
      "Maintaining heritage and quality is our sacred duty.",
      "Traditional excellence meets contemporary standards in our work."
    ]
  }
};

// Service-specific unique selling points
const SERVICE_USP = {
  decoration: [
    "custom themes designed to match your vision",
    "fresh flowers sourced daily for maximum beauty",
    "eco-friendly decoration materials that look stunning",
    "complete setup and cleanup handled by our expert team",
    "themed decoration that tells your unique story"
  ],
  venue: [
    "spacious venue with flexible seating arrangements",
    "modern amenities with traditional charm",
    "ample parking space for all your guests",
    "beautiful location perfect for memorable photos",
    "customizable layouts to fit your event perfectly"
  ],
  infrastructure: [
    "weather-resistant equipment for all seasons",
    "professional installation ensuring safety first",
    "backup systems to prevent any disruptions",
    "various sizes available to match your requirements",
    "latest equipment maintained to highest standards"
  ],
  catering: [
    "freshly prepared food with authentic flavors",
    "customizable menus for all dietary preferences",
    "hygienic kitchen practices with FSSAI standards",
    "live counters for interactive dining experience",
    "taste testing available before booking"
  ],
  photography: [
    "cinematic storytelling through our lens",
    "latest 4K cameras and drone technology",
    "edited photos delivered within promised timeline",
    "candid moments captured naturally",
    "professional albums and unique photo frames"
  ],
  makeup: [
    "premium branded products for lasting beauty",
    "pre-bridal trials to perfect your look",
    "personalized styling matching your personality",
    "waterproof makeup lasting entire day",
    "complimentary touch-ups during the event"
  ],
  entertainment: [
    "extensive song library with latest hits",
    "professional sound quality without distortion",
    "interactive sessions keeping guests engaged",
    "customized playlists matching your taste",
    "experienced performers reading the crowd perfectly"
  ],
  lighting: [
    "synchronized lighting creating magical ambiance",
    "energy-efficient LED systems with various colors",
    "wireless controls for dynamic light shows",
    "safe installation meeting all regulations",
    "dramatic effects transforming your venue"
  ],
  transportation: [
    "well-maintained vehicles in excellent condition",
    "experienced drivers familiar with local routes",
    "punctual service ensuring guests arrive on time",
    "comfortable seating for a pleasant journey",
    "elegantly decorated vehicles for special occasions"
  ],
  religious: [
    "authentic rituals performed with proper vedic knowledge",
    "all necessary puja materials included",
    "guidance on muhurat and auspicious timings",
    "respectful ceremonies honoring traditions",
    "multilingual services for diverse communities"
  ],
  rental: [
    "premium quality items in pristine condition",
    "wide variety matching different themes",
    "hassle-free delivery and pickup",
    "affordable pricing with transparent policies",
    "sanitized items ensuring complete hygiene"
  ],
  gifts: [
    "unique and thoughtful gift selections",
    "beautiful packaging creating lasting impressions",
    "personalization options available",
    "bulk orders managed efficiently",
    "timely delivery guaranteed"
  ],
  security: [
    "trained professionals ensuring complete safety",
    "discreet yet effective security presence",
    "crowd management with courteous approach",
    "emergency response coordination",
    "CCTV monitoring for comprehensive coverage"
  ],
  other: [
    "attention to every small detail",
    "reliable service you can count on",
    "creative solutions for unique requirements",
    "transparent pricing with no hidden costs",
    "dedicated support throughout your event"
  ]
};

// Location-based descriptions
const LOCATION_PHRASES = [
  "Proudly serving {city} and surrounding areas",
  "Based in {city}, serving the entire {state} region",
  "Your trusted partner in {city} for all event needs",
  "Bringing excellence to {city} since our inception",
  "Serving {city} with dedication and passion"
];

// Team descriptions
const TEAM_DESCRIPTIONS = [
  "Our skilled team works tirelessly to exceed expectations.",
  "Each team member brings unique expertise and creativity.",
  "Our dedicated professionals ensure flawless execution.",
  "We have a passionate team committed to your satisfaction.",
  "Expert craftsmen and coordinators handle every detail."
];

// Closing statements
const CLOSING_STATEMENTS = [
  "Book early to ensure availability for your special dates!",
  "Contact us to discuss your requirements and get a customized quote.",
  "Let's make your event unforgettable together!",
  "We look forward to being part of your celebrations.",
  "Get in touch today for a free consultation!",
  "Reserve your slot now and get special packages!",
  "Call us to transform your vision into reality.",
  "Experience the difference of working with true professionals."
];

const HINDI_WRITING_STYLES = {
  professional: {
    intro: [
      "हम",
      "हमारी टीम",
      "हमारा व्यवसाय",
      "हम आपके लिए",
      "हम विशेष रूप से"
    ],
    experience: [
      "{exp} वर्षों के अनुभव के साथ हम",
      "हमारे {exp}+ वर्षों के अनुभव ने हमें",
      "{exp} साल से काम करते हुए हमने",
      "पिछले {exp} वर्षों में हमने"
    ],
    quality: [
      "हम हर काम में गुणवत्ता, समय पर सेवा और ग्राहक संतुष्टि को सबसे पहले रखते हैं।",
      "हमारी प्राथमिकता साफ-सुथरा काम, भरोसेमंद सेवा और बेहतरीन अनुभव देना है।",
      "हम हर प्रोजेक्ट को जिम्मेदारी और पूरी लगन के साथ पूरा करते हैं।"
    ]
  },
  friendly: {
    intro: [
      "हम खूबसूरत",
      "हम यादगार",
      "हम आपके खास दिन के लिए",
      "हम खुशी के मौकों के लिए",
      "हम बेहतरीन"
    ],
    experience: [
      "{exp} वर्षों से हम लोगों के खास मौकों को बेहतर बना रहे हैं और",
      "पिछले {exp} सालों से हम लगातार",
      "{exp} वर्षों के अनुभव के कारण हम अच्छी तरह जानते हैं कि",
      "{exp} साल के काम ने हमें सिखाया है कि"
    ],
    quality: [
      "आपकी खुशी और भरोसा ही हमारी सबसे बड़ी ताकत है।",
      "हम हर काम को अपने परिवार के कार्यक्रम जैसा महत्व देते हैं।",
      "आपके खास मौके को आसान और सुंदर बनाना हमारा उद्देश्य है।"
    ]
  },
  traditional: {
    intro: [
      "हम पारंपरिक और आधुनिक अंदाज के साथ",
      "हमारी सेवा में संस्कृति और गुणवत्ता के साथ",
      "हम भरोसेमंद और व्यवस्थित तरीके से",
      "हम परंपरा और अनुभव के साथ",
      "हम आपके लिए सच्ची लगन से"
    ],
    experience: [
      "{exp} वर्षों के अनुभव के आधार पर हम",
      "हमारी {exp} साल की सेवा यात्रा ने हमें",
      "पिछले {exp} वर्षों से हम निरंतर",
      "{exp} वर्षों में हमने मजबूत अनुभव के साथ"
    ],
    quality: [
      "हम काम में परंपरा, विश्वास और गुणवत्ता का संतुलन बनाए रखते हैं।",
      "हमारी सेवा में साफ-सफाई, अनुशासन और भरोसेमंद व्यवस्था शामिल है।",
      "हम हर विवरण का ध्यान रखकर बेहतर परिणाम देने की कोशिश करते हैं।"
    ]
  }
};

const HINDI_SERVICE_USP = {
  decoration: [
    "आपकी पसंद के अनुसार थीम आधारित सजावट",
    "ताजा फूलों और आकर्षक सामग्री से सुंदर डेकोरेशन",
    "सेटअप से लेकर समापन तक पूरी जिम्मेदारी हमारी टीम संभालती है",
    "कार्यक्रम के अनुसार कस्टमाइज्ड सजावट की सुविधा"
  ],
  venue: [
    "आरामदायक और व्यवस्थित बैठने की सुविधा",
    "परिवार और मेहमानों के लिए उपयुक्त साफ-सुथरी जगह",
    "कार्यक्रम के अनुसार लेआउट और व्यवस्था में लचीलापन",
    "यादगार अवसरों के लिए सुंदर और उपयोगी स्थान"
  ],
  infrastructure: [
    "मजबूत और भरोसेमंद सेटअप व्यवस्था",
    "सुरक्षित इंस्टॉलेशन और समय पर तैयारी",
    "जरूरत के अनुसार अलग-अलग आकार और व्यवस्था उपलब्ध",
    "कार्यक्रम बिना रुकावट चले इसके लिए व्यवस्थित तैयारी"
  ],
  catering: [
    "ताजा और स्वादिष्ट भोजन की व्यवस्था",
    "जरूरत के अनुसार मेनू कस्टमाइज करने की सुविधा",
    "साफ-सफाई और गुणवत्ता का विशेष ध्यान",
    "मेहमानों के अनुभव को बेहतर बनाने वाली सर्विस"
  ],
  photography: [
    "खास पलों को सुंदर तरीके से कैद करने की सेवा",
    "कैंडिड और पारंपरिक दोनों तरह की फोटोग्राफी",
    "बेहतर क्वालिटी और समय पर डिलीवरी",
    "यादगार एल्बम और आकर्षक फोटो प्रेजेंटेशन"
  ],
  makeup: [
    "चेहरे और मौके के अनुसार पर्सनलाइज्ड मेकअप",
    "लंबे समय तक टिकने वाला प्रोफेशनल मेकअप",
    "प्रीमियम प्रोडक्ट्स के साथ साफ-सुथरी सेवा",
    "आपके लुक को खास बनाने पर पूरा ध्यान"
  ],
  entertainment: [
    "मेहमानों को जोड़े रखने वाली मनोरंजन सेवा",
    "मौके के अनुसार गानों और कार्यक्रम की तैयारी",
    "बेहतर साउंड और सक्रिय प्रस्तुति",
    "भीड़ के माहौल के अनुसार कार्यक्रम संचालित करने का अनुभव"
  ],
  lighting: [
    "सुंदर माहौल बनाने वाली लाइटिंग व्यवस्था",
    "मौके के अनुसार रंग और इफेक्ट की सुविधा",
    "सुरक्षित और व्यवस्थित इंस्टॉलेशन",
    "सजावट को और आकर्षक बनाने वाली रोशनी"
  ],
  transportation: [
    "समय पर और भरोसेमंद वाहन सेवा",
    "आरामदायक यात्रा के लिए अच्छी व्यवस्था",
    "स्थानीय मार्गों की जानकारी रखने वाले ड्राइवर",
    "विशेष अवसरों के लिए साफ और व्यवस्थित वाहन"
  ],
  religious: [
    "रीति-रिवाज के अनुसार श्रद्धापूर्वक सेवा",
    "पूजा-सामग्री और आवश्यक व्यवस्था में सहयोग",
    "मौके के अनुसार उचित समय और विधि की जानकारी",
    "परंपरा और सम्मान के साथ आयोजन"
  ],
  rental: [
    "साफ और अच्छी स्थिति में उपलब्ध सामान",
    "अलग-अलग जरूरत के अनुसार विकल्प उपलब्ध",
    "डिलीवरी और पिकअप की आसान सुविधा",
    "उचित दरों पर भरोसेमंद सेवा"
  ],
  gifts: [
    "आकर्षक और उपयोगी गिफ्ट विकल्प",
    "सुंदर पैकिंग और समय पर उपलब्धता",
    "बड़े ऑर्डर की व्यवस्थित तैयारी",
    "जरूरत के अनुसार कस्टमाइजेशन की सुविधा"
  ],
  security: [
    "सुरक्षा और व्यवस्था बनाए रखने वाली प्रशिक्षित टीम",
    "भीड़ प्रबंधन और जिम्मेदार निगरानी",
    "कार्यक्रम स्थल की सुरक्षित व्यवस्था",
    "जरूरत पड़ने पर तुरंत सहायता और समन्वय"
  ],
  other: [
    "हर छोटे-बड़े काम में ध्यान और जिम्मेदारी",
    "भरोसेमंद और समय पर सेवा",
    "आपकी जरूरत के अनुसार लचीला समाधान",
    "स्पष्ट और ईमानदार कामकाज"
  ]
};

const HINDI_LOCATION_PHRASES = [
  "हम {city} और आसपास के क्षेत्रों में अपनी सेवाएं प्रदान करते हैं",
  "{city} में आधारित हमारी सेवा {state} क्षेत्र तक उपलब्ध है",
  "{city} में आपकी जरूरतों के लिए हम एक भरोसेमंद विकल्प हैं",
  "हम {city} में समर्पण और विश्वास के साथ सेवा दे रहे हैं"
];

const HINDI_TEAM_DESCRIPTIONS = [
  "हमारी टीम हर काम को समय पर और व्यवस्थित तरीके से पूरा करने के लिए प्रतिबद्ध है।",
  "अनुभवी और सहयोगी टीम के साथ हम बेहतर सेवा देने का प्रयास करते हैं।",
  "हमारी टीम हर विवरण पर ध्यान देकर काम करती है ताकि आपको परेशानी न हो।"
];

const HINDI_CLOSING_STATEMENTS = [
  "अपने खास दिन के लिए समय रहते बुकिंग करें।",
  "अपनी जरूरत बताइए, हम आपके लिए उपयुक्त सेवा तैयार करेंगे।",
  "हम आपके कार्यक्रम को आसान, सुंदर और यादगार बनाने के लिए तैयार हैं।",
  "आज ही संपर्क करें और अपनी सुविधा के अनुसार जानकारी प्राप्त करें।"
];

const MIXED_WRITING_STYLES = {
  intro: [
    "हम आपके लिए reliable",
    "हम quality",
    "हम affordable और professional",
    "हम trusted",
    "हम अच्छी planning के साथ"
  ],
  experience: [
    "{exp} years के experience के साथ हम",
    "हमारे {exp}+ साल के experience ने हमें",
    "{exp} साल से काम करते हुए हमने",
    "पिछले {exp} वर्षों में हमने"
  ],
  quality: [
    "हम quality, timely service और customer satisfaction पर पूरा ध्यान देते हैं।",
    "हमारा focus साफ-सुथरा काम, proper management और trusted service देना है।",
    "हम हर event को जिम्मेदारी और planning के साथ handle करते हैं।"
  ]
};

const MIXED_LOCATION_PHRASES = [
  "हम {city} और आसपास के area में अपनी service देते हैं",
  "{city} based होकर हम {state} region तक service provide करते हैं",
  "{city} में event needs के लिए हम एक trusted option हैं"
];

const MIXED_TEAM_DESCRIPTIONS = [
  "हमारी team हर काम को proper coordination के साथ पूरा करती है।",
  "Experienced team के साथ हम smooth service देने की कोशिश करते हैं।",
  "हम detail पर ध्यान देकर आपका काम easy और tension-free बनाते हैं।"
];

const MIXED_CLOSING_STATEMENTS = [
  "अपने special day के लिए time पर booking कर लें।",
  "अपनी requirement बताइए, हम best option suggest करेंगे।",
  "हम आपके event को simple, सुंदर और memorable बनाने के लिए ready हैं।"
];

/**
 * Randomly select an item from array
 */
const randomPick = (array) => {
  return array[Math.floor(Math.random() * array.length)];
};

const buildServiceText = (serviceLabels, language = "en") => {
  if (serviceLabels.length === 1) {
    return serviceLabels[0];
  }

  if (serviceLabels.length === 2) {
    return language === "hi"
      ? `${serviceLabels[0]} और ${serviceLabels[1]}`
      : serviceLabels.join(" and ");
  }

  const lastService = serviceLabels[serviceLabels.length - 1];
  const otherServices = serviceLabels.slice(0, -1).join(", ");
  return language === "hi"
    ? `${otherServices} और ${lastService}`
    : `${otherServices}, and ${lastService}`;
};

/**
 * Get category-specific USPs based on service type
 */
const getCategoryUSPs = (serviceCategories) => {
  const usps = new Set();
  
  serviceCategories.forEach(service => {
    const category = service.category?.toLowerCase() || 'other';
    
    if (SERVICE_USP[category]) {
      // Pick 1-2 random USPs for each category
      const pickCount = Math.random() > 0.5 ? 2 : 1;
      for (let i = 0; i < pickCount; i++) {
        usps.add(randomPick(SERVICE_USP[category]));
      }
    } else {
      usps.add(randomPick(SERVICE_USP.other));
    }
  });
  
  return Array.from(usps);
};

const getCategoryUSPsHindi = (serviceCategories) => {
  const usps = new Set();

  serviceCategories.forEach(service => {
    const category = service.category?.toLowerCase() || 'other';

    if (HINDI_SERVICE_USP[category]) {
      const pickCount = Math.random() > 0.5 ? 2 : 1;
      for (let i = 0; i < pickCount; i++) {
        usps.add(randomPick(HINDI_SERVICE_USP[category]));
      }
    } else {
      usps.add(randomPick(HINDI_SERVICE_USP.other));
    }
  });

  return Array.from(usps);
};

const generateHindiDescription = ({
  serviceCategories = [],
  businessName = "",
  experience = 0,
  city = "",
  state = "",
}) => {
  if (!serviceCategories || serviceCategories.length === 0) {
    return "";
  }

  const styleKeys = Object.keys(HINDI_WRITING_STYLES);
  const selectedStyle = HINDI_WRITING_STYLES[randomPick(styleKeys)];
  const serviceLabels = serviceCategories.map(s => s.label || s.value).filter(Boolean);
  const serviceText = buildServiceText(serviceLabels, "hi");
  const description = [];

  description.push(`${randomPick(selectedStyle.intro)} ${serviceText} सेवाएं प्रदान करते हैं।`);

  if (experience && experience > 0) {
    const expText = randomPick(selectedStyle.experience).replace('{exp}', experience);
    description.push(`${expText} बेहतर और भरोसेमंद सेवा देना सीखा है।`);
  }

  if (businessName && Math.random() > 0.4) {
    description.push(`${businessName} में हम हर ग्राहक की जरूरत को समझकर काम करते हैं।`);
  }

  const usps = getCategoryUSPsHindi(serviceCategories);
  if (usps.length === 1) {
    description.push(`हमारी सेवा में ${usps[0]} शामिल है।`);
  } else if (usps.length === 2) {
    description.push(`हम ${usps[0]} और ${usps[1]} जैसी सुविधाएं देते हैं।`);
  } else if (usps.length >= 3) {
    description.push(`हमारी सेवाओं में ${usps[0]}, ${usps[1]} और ${usps[2]} शामिल हैं।`);
  }

  description.push(randomPick(selectedStyle.quality));

  if (city && Math.random() > 0.3) {
    const locationText = randomPick(HINDI_LOCATION_PHRASES)
      .replace('{city}', city)
      .replace('{state}', state || 'इस क्षेत्र');
    description.push(`${locationText}।`);
  }

  if (Math.random() > 0.5) {
    description.push(randomPick(HINDI_TEAM_DESCRIPTIONS));
  }

  description.push(randomPick(HINDI_CLOSING_STATEMENTS));
  return description.join(" ");
};

const generateMixedDescription = ({
  serviceCategories = [],
  businessName = "",
  experience = 0,
  city = "",
  state = "",
}) => {
  if (!serviceCategories || serviceCategories.length === 0) {
    return "";
  }

  const serviceLabels = serviceCategories.map(s => s.label || s.value).filter(Boolean);
  const serviceText = buildServiceText(serviceLabels, "hi");
  const description = [];

  description.push(`${randomPick(MIXED_WRITING_STYLES.intro)} ${serviceText} services provide करते हैं।`);

  if (experience && experience > 0) {
    const expText = randomPick(MIXED_WRITING_STYLES.experience).replace('{exp}', experience);
    description.push(`${expText} बेहतर service देना सीखा है।`);
  }

  if (businessName && Math.random() > 0.4) {
    description.push(`${businessName} में हम customer की जरूरत समझकर proper service देने पर focus करते हैं।`);
  }

  const usps = getCategoryUSPsHindi(serviceCategories);
  if (usps.length === 1) {
    description.push(`हमारी service में ${usps[0]} शामिल है।`);
  } else if (usps.length === 2) {
    description.push(`हम ${usps[0]} और ${usps[1]} जैसी सुविधाएं provide करते हैं।`);
  } else if (usps.length >= 3) {
    description.push(`हमारी services में ${usps[0]}, ${usps[1]} और ${usps[2]} शामिल हैं।`);
  }

  description.push(randomPick(MIXED_WRITING_STYLES.quality));

  if (city && Math.random() > 0.3) {
    const locationText = randomPick(MIXED_LOCATION_PHRASES)
      .replace('{city}', city)
      .replace('{state}', state || 'local');
    description.push(`${locationText}।`);
  }

  if (Math.random() > 0.5) {
    description.push(randomPick(MIXED_TEAM_DESCRIPTIONS));
  }

  description.push(randomPick(MIXED_CLOSING_STATEMENTS));
  return description.join(" ");
};

/**
 * Generate unique AI-powered service description
 */
export const generateAIDescription = ({
  serviceCategories = [],
  businessName = "",
  experience = 0,
  city = "",
  state = "",
  ownerName = "",
  language = "en"
}) => {
  // Validation
  if (!serviceCategories || serviceCategories.length === 0) {
    return "";
  }

  if (language === "hi") {
    return generateHindiDescription({
      serviceCategories,
      businessName,
      experience,
      city,
      state,
      ownerName,
    });
  }

  if (language === "mix") {
    return generateMixedDescription({
      serviceCategories,
      businessName,
      experience,
      city,
      state,
      ownerName,
    });
  }

  // Select a random writing style to ensure variety
  const styleKeys = Object.keys(WRITING_STYLES);
  const selectedStyle = WRITING_STYLES[randomPick(styleKeys)];
  
  // Build service list
  const serviceLabels = serviceCategories.map(s => s.label || s.value);
  const serviceText = buildServiceText(serviceLabels, "en");
  
  // Build description parts
  let description = [];
  
  // 1. Introduction
  const intro = `${randomPick(selectedStyle.intro)} ${serviceText} services.`;
  description.push(intro);
  
  // 2. Experience (if available)
  if (experience && experience > 0) {
    const expText = randomPick(selectedStyle.experience).replace('{exp}', experience);
    description.push(`${expText} exceptional results.`);
  }
  
  // 3. Business identity (occasionally mention business name)
  if (businessName && Math.random() > 0.6) {
    description.push(`At ${businessName}, we believe in creating memories that last a lifetime.`);
  }
  
  // 4. Service-specific USPs (2-3 points)
  const usps = getCategoryUSPs(serviceCategories);
  if (usps.length > 0) {
    const uspCount = Math.min(3, usps.length);
    const selectedUSPs = usps.slice(0, uspCount);
    
    if (selectedUSPs.length === 1) {
      description.push(`We offer ${selectedUSPs[0]}.`);
    } else if (selectedUSPs.length === 2) {
      description.push(`We provide ${selectedUSPs[0]} and ${selectedUSPs[1]}.`);
    } else {
      description.push(`Our services include ${selectedUSPs[0]}, ${selectedUSPs[1]}, and ${selectedUSPs[2]}.`);
    }
  }
  
  // 5. Quality commitment
  description.push(randomPick(selectedStyle.quality));
  
  // 6. Location (if available)
  if (city && Math.random() > 0.4) {
    const locationText = randomPick(LOCATION_PHRASES)
      .replace('{city}', city)
      .replace('{state}', state || 'the region');
    description.push(locationText + ".");
  }
  
  // 7. Team mention (occasionally)
  if (Math.random() > 0.5) {
    description.push(randomPick(TEAM_DESCRIPTIONS));
  }
  
  // 8. Closing statement
  description.push(randomPick(CLOSING_STATEMENTS));
  
  // Join all parts
  return description.join(" ");
};

/**
 * Simulate API call with slight delay (for future integration with real AI APIs)
 */
export const generateAIDescriptionAsync = async (params) => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
  
  return generateAIDescription(params);
};

/**
 * For future OpenAI integration (placeholder)
 */
export const generateWithOpenAI = async (params) => {
  // TODO: Integrate with OpenAI API
  // const response = await fetch('https://api.openai.com/v1/chat/completions', {
  //   method: 'POST',
  //   headers: {
  //     'Content-Type': 'application/json',
  //     'Authorization': `Bearer ${OPENAI_API_KEY}`
  //   },
  //   body: JSON.stringify({
  //     model: 'gpt-3.5-turbo',
  //     messages: [{ role: 'user', content: prompt }]
  //   })
  // });
  
  // For now, use our internal generator
  return generateAIDescriptionAsync(params);
};
